import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { OwcList, OwcListItem } from '@one/react';
import { getVirtualRange } from '../../utils/general/virtualList';
import styles from './SectionAccordion.module.css';

/**
 * Row height must match `.listItem` in the CSS module: the virtual scroller
 * positions rows arithmetically, so the rendered height has to be fixed.
 */
const ITEM_HEIGHT = 24;
const VIEWPORT_HEIGHT = 200;
const OVERSCAN = 8;
/** Below this many items the DOM cost is negligible; render the list as-is. */
const VIRTUALIZE_ABOVE = 40;

/** Item key; includes the parent so same-named items under different parents differ. */
const itemKey = (type, item) =>
  item.parent ? `${type}-${item.parent}/${item.name}` : `${type}-${item.name}`;

function SectionAccordion({
  title,
  items = [],
  visible,
  sectionId,
  onToggle,
  type,
  activeKey,
  setActiveKey,
  setActiveParent,
  highlight,
  setSchemaError,
  setSchemaData,
  setSchemaSource,
  groupName,
}) {
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);

  const itemCount = items.length;
  const virtualized = itemCount > VIRTUALIZE_ABOVE;

  const { startIndex, endIndex, totalHeight } = useMemo(() => {
    if (!virtualized) {
      return { startIndex: 0, endIndex: itemCount, totalHeight: itemCount * ITEM_HEIGHT };
    }
    return getVirtualRange({
      itemCount,
      scrollTop,
      itemHeight: ITEM_HEIGHT,
      viewportHeight: VIEWPORT_HEIGHT,
      overscan: OVERSCAN,
    });
  }, [virtualized, itemCount, scrollTop]);

  const visibleItems = useMemo(
    () => (virtualized ? items.slice(startIndex, endIndex) : items),
    [virtualized, items, startIndex, endIndex]
  );

  // A new list (search, subgroup or release change) starts at the top.
  useEffect(() => {
    setScrollTop(0);
    if (viewportRef.current) viewportRef.current.scrollTop = 0;
  }, [items]);

  // Keep the selected item on screen — with virtualization it is otherwise
  // possible for the active row to exist only in the spacer.
  useEffect(() => {
    if (!visible || !virtualized || !activeKey) return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    const index = items.findIndex((item) => itemKey(type, item) === activeKey);
    if (index < 0) return;

    const top = index * ITEM_HEIGHT;
    const isVisible =
      top >= viewport.scrollTop && top + ITEM_HEIGHT <= viewport.scrollTop + VIEWPORT_HEIGHT;
    if (isVisible) return;

    const next = Math.max(0, top - VIEWPORT_HEIGHT / 2);
    viewport.scrollTop = next;
    setScrollTop(next);
  }, [visible, virtualized, activeKey, items, type]);

  // `owc-list`/`owc-list-item` are custom elements: when a panel is expanded the
  // viewport is measured before they upgrade, so the rows have no laid-out height
  // and the panel reads as empty until a scroll event invalidates it. Wait for the
  // definitions to upgrade, then force one layout read.
  useEffect(() => {
    if (!visible || itemCount === 0) return;
    let cancelled = false;

    const reflow = () => {
      if (cancelled) return;
      const viewport = viewportRef.current;
      if (viewport) void viewport.offsetHeight;
    };

    const defined = window.customElements
      ? Promise.all([
          window.customElements.whenDefined('owc-list'),
          window.customElements.whenDefined('owc-list-item'),
        ])
      : Promise.resolve();

    defined.then(() => requestAnimationFrame(reflow)).catch(reflow);

    return () => {
      cancelled = true;
    };
  }, [visible, itemCount]);

  const handleScroll = useCallback((event) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  const handleSelect = useCallback(
    (item) => {
      // A complex data type with no parent cannot be resolved to a schema path.
      if (type === 'complexDataType' && !item.parent?.trim()) {
        setSchemaError(`The format of [${item.name}] is invalid.`);
        setSchemaData(null);
        setSchemaSource({ type, name: item.name, parent: item.parent || '' });
        return;
      }

      setActiveKey(itemKey(type, item));
      if (setActiveParent) setActiveParent(item.parent || '');
      window.dispatchEvent(new CustomEvent('focus-release-dropdown'));

      const parentParts =
        type === 'complexDataType' || !item.parent
          ? []
          : item.parent.split('/').map(encodeURIComponent);
      const segments = [
        encodeURIComponent(groupName),
        encodeURIComponent(type),
        ...parentParts,
        encodeURIComponent(item.name),
      ]
        .filter(Boolean)
        .join('/');
      navigate(`/dashboard/${segments}`);
    },
    [
      type,
      groupName,
      navigate,
      setActiveKey,
      setActiveParent,
      setSchemaData,
      setSchemaError,
      setSchemaSource,
    ]
  );

  const handleToggle = useCallback(() => onToggle(sectionId), [onToggle, sectionId]);

  return (
    <>
      <div className={styles.sectionLabel} onClick={handleToggle}>
        {title} ({itemCount})<span className={styles.accordionIcon}>{visible ? '−' : '+'}</span>
      </div>

      {/* Collapsed sections render no rows at all: a group such as MolLab has
          seven sections, and mounting every list is what makes the drawer lag. */}
      {visible && (
        <div className={styles.accordionPanel}>
          {itemCount === 0 ? (
            <div className={styles.emptyText}>No {title} Available</div>
          ) : (
            <div className={styles.listViewport} ref={viewportRef} onScroll={handleScroll}>
              <div
                style={{
                  height: virtualized ? totalHeight : undefined,
                  position: 'relative',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    minWidth: 0,
                    transform: virtualized
                      ? `translateY(${startIndex * ITEM_HEIGHT}px)`
                      : undefined,
                  }}
                >
                  <OwcList noBorder size="small">
                    {visibleItems.map((item) => {
                      const uniqueId = itemKey(type, item);
                      const isSelected = activeKey === uniqueId;

                      return (
                        <OwcListItem
                          key={uniqueId}
                          value={uniqueId}
                          selected={isSelected}
                          title={item.description || item.name}
                          onClick={() => handleSelect(item)}
                          className={`${styles.listItem} ${isSelected ? styles.selected : ''}`}
                        >
                          {highlight ? highlight(item.name) : item.name}
                        </OwcListItem>
                      );
                    })}
                  </OwcList>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default React.memo(SectionAccordion);
